/**
 * OptionCard — a single selectable option in a wizard question.
 *
 * Mobile-first row layout matching the reference screenshots:
 *  - Leading icon or numeric badge
 *  - Title + optional subtitle
 *  - Selected state = primary-tinted background + primary border + right-
 *    side check icon
 *
 * Used for MCQ questions (quiz) and most benchmark questions.
 */

import type { ReactNode } from 'react';
import { Check } from 'lucide-react';
import './OptionCard.css';

export type OptionCardProps = {
    /** rendered at the left (icon or "1" or initial letter) */
    leading?: ReactNode;
    /** main label */
    label: string;
    /** optional muted subtitle under the label */
    subtitle?: string;
    /** optional extra muted line (e.g. year range, category) */
    meta?: string;
    /** selected state */
    selected: boolean;
    /** tap handler */
    onSelect: () => void;
    /** disabled (e.g. submission in flight) */
    disabled?: boolean;
};

function OptionCard({ leading, label, subtitle, meta, selected, onSelect, disabled }: OptionCardProps) {
    return (
        <button
            type="button"
            role="radio"
            aria-checked={selected ? 'true' : 'false'}
            className={`wizard-option${selected ? ' wizard-option--selected' : ''}`}
            onClick={onSelect}
            disabled={disabled}
        >
            {leading && <span className="wizard-option__leading" aria-hidden="true">{leading}</span>}
            <span className="wizard-option__body">
                <span className="wizard-option__label">{label}</span>
                {subtitle && <span className="wizard-option__subtitle">{subtitle}</span>}
                {meta && <span className="wizard-option__meta">{meta}</span>}
            </span>
            <span className="wizard-option__check" aria-hidden="true">
                {selected && <Check size={16} />}
            </span>
        </button>
    );
}

export default OptionCard;
